import api from '@/shared/api';
import type { ApiResponse } from '@/shared/lib';
import { translateSettingsApiError } from './settings-list-api.factory';
import type { SettingsListQuery } from '../lib/settings-list-query';
import type {
  RecordOriginItem,
  RecordOriginPayload,
  SettingsListItemCreated,
} from '../model/settings.types';

const BASE_PATH = '/record-origins';

/**
 * Written by hand, and not built by `createSettingsListApi`, because the contract of this entity is
 * not the contract of the other four: the payload carries the two sets of linked identifiers and
 * one name, never a batch of names. What it does reuse is the translation of a rejection, so the
 * duplicate name and the refused validation reach the modal in the same shape as everywhere else.
 */
export const recordOriginsApi = {
  list: async (query: SettingsListQuery = {}): Promise<RecordOriginItem[]> => {
    const response = await api.get<ApiResponse<RecordOriginItem[]>>(BASE_PATH, { params: query });

    return response.data.data ?? [];
  },

  create: async (payload: RecordOriginPayload): Promise<SettingsListItemCreated | null> => {
    try {
      const response = await api.post<ApiResponse<SettingsListItemCreated>>(BASE_PATH, payload);

      return response.data.data ?? null;
    } catch (error) {
      throw translateSettingsApiError(error);
    }
  },

  update: async (
    id: number,
    payload: RecordOriginPayload
  ): Promise<SettingsListItemCreated | null> => {
    try {
      const response = await api.patch<ApiResponse<SettingsListItemCreated>>(
        `${BASE_PATH}/${id}`,
        payload
      );

      return response.data.data ?? null;
    } catch (error) {
      throw translateSettingsApiError(error);
    }
  },

  // The API answers 204 with no body, so the caller has to be told the row is gone explicitly.
  remove: async (id: number): Promise<boolean> => {
    await api.delete(`${BASE_PATH}/${id}`);

    return true;
  },
};
